import { useCallback, useEffect, useMemo, useState } from "react";
import { axiosInstance } from "@/utilities";
import { API_URL_PIM_DRAFTS, API_URL_PIM_PRODUCTS } from "@/services/XCONS_ENV";
import { EstadoPublicado, esEstadoPublicado } from "./estados";
import { filtraPorIndice } from "./filtros-indice";
import type {
  NivelCatalogo,
  CatalogHit,
  CatalogSearchResponse,
  DraftRow,
} from "./types";
import { FILAS_POR_PAGINA } from "@/config/paginacion";

export interface CatalogFilters {
  search: string;
  manufacturer?: string;
  attributeSet?: string;
  /**
   * El estado de la columna, que ahora cruza las dos fuentes: `draft` y `review` viven en
   * la base del admin, `active`/`inactive`/`archived` en el índice (su campo `state`,
   * xcons-docs#380). Es lo que reemplazó a las pestañas — un filtro que se saca con un
   * click, y no una elección previa que hay que acertar.
   */
  estado?: "draft" | "review" | EstadoPublicado;
  /**
   * Opciones elegidas por atributo de tipo lista, `código → optionIds` (xcons-docs#434). En
   * un mismo atributo es OR ("Color: Blanco o Negro") y entre atributos es AND.
   */
  atributos?: Record<string, number[]>;
  /** Categorías del árbol de XCONS: OR entre ellas, y cada una incluye sus subcategorías. */
  categorias?: number[];
  /**
   * Si el producto tiene dueño (xcons-docs#447): la faceta `hasOwner` del índice. Un borrador
   * siempre tiene dueño —quien lo creó—, así que es un filtro de lo publicado.
   */
  dueno?: "sin" | "con";
}

export { filtraPorIndice } from "./filtros-indice";

const EMPTY_FACETS: Record<string, Record<string, number>> = {};

/**
 * El estado del listado del catálogo.
 *
 * **No usa `useTable` de Refine y no es por gusto:** el listado de publicados sale del
 * índice main, así que la respuesta es la de Algolia (`hits`, `nbHits`, `facets`) y no el
 * `{ data, total }` que espera el dataProvider. Y los borradores salen de otro endpoint
 * con otra forma. Envolver las dos en un dataProvider sería inventar una capa para tapar
 * que son dos fuentes distintas — que es justamente lo que las tres vistas separadas
 * hacen explícito.
 */
/**
 * **Los filtros del listado publicado como los pide el API**, en un solo lugar: los usan el
 * listado y el export (xcons-docs#467), así que lo que se exporta es lo que se ve filtrado.
 */
export function filtrosDelListado(filters: CatalogFilters): {
  search?: string;
  facetFilters: string[];
  attributeFilters: string[];
  categoryIds: number[];
} {
  const facetFilters: string[] = [];
  if (filters.manufacturer) {
    facetFilters.push(`manufacturer:${filters.manufacturer}`);
  }
  if (filters.attributeSet) {
    facetFilters.push(`attributeSet:${filters.attributeSet}`);
  }
  // El filtro de estado sólo llega a Algolia cuando apunta a un estado publicado: los
  // otros dos valores no existen en el índice, porque los borradores no se indexan.
  // **`state` y no `status`**: `status:2` eran Pausado y Archivado juntos.
  if (esEstadoPublicado(filters.estado)) {
    facetFilters.push(`state:${filters.estado}`);
  }
  if (filters.dueno) {
    facetFilters.push(`hasOwner:${filters.dueno === "con"}`);
  }

  // `código:id,id` por atributo: el API lo arma como un grupo OR, y varios grupos van en
  // AND (xcon-api!1019). Se manda con la clave repetida, igual que `facetFilters`.
  const attributeFilters = Object.entries(filters.atributos ?? {})
    .filter(([, ids]) => ids.length)
    .map(([code, ids]) => `${code}:${ids.join(",")}`);
  const categoryIds = filters.categorias ?? [];

  return {
    search: filters.search || undefined,
    facetFilters,
    attributeFilters,
    categoryIds,
  };
}

export const useCatalogList = () => {
  const [loading, setLoading] = useState(false);
  /**
   * Hasta que termina la primera carga no se sabe el nivel: sin esto la pantalla arrancaba
   * como `viewer`, sin Permisos ni Nuevo producto, y con el total de los borradores, que
   * llegan antes (xcons-docs#487).
   */
  const [primeraCarga, setPrimeraCarga] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [hits, setHits] = useState<CatalogHit[]>([]);
  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const [nivel, setNivel] = useState<NivelCatalogo>("viewer");
  const [total, setTotal] = useState(0);
  const [facets, setFacets] =
    useState<Record<string, Record<string, number>>>(EMPTY_FACETS);

  /**
   * La cola de aprobación, para el badge del segmented. Va por su propia ruta y no sale del
   * `total` del listado porque la cola es de TODOS: el contador que el aprobador tiene que
   * ver no puede depender de cómo esté filtrando la tabla, ni de en qué vista esté parado.
   */
  const [queueCount, setQueueCount] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(FILAS_POR_PAGINA);
  const [filters, setFilters] = useState<CatalogFilters>({ search: "" });

  const fetchPublished = useCallback(async () => {
    const { facetFilters, attributeFilters, categoryIds } = filtrosDelListado(filters);
    const { data } = await axiosInstance.get<CatalogSearchResponse>(
      API_URL_PIM_PRODUCTS,
      {
        params: {
          search: filters.search || undefined,
          facetFilters: facetFilters.length ? facetFilters : undefined,
          attributeFilters: attributeFilters.length ? attributeFilters : undefined,
          categoryIds: categoryIds.length ? categoryIds : undefined,
          hitsPerPage: pageSize,
          page,
        },
        // **Sin esto ningún filtro funciona.** Axios serializa un array como
        // `facetFilters[]=a&facetFilters[]=b`, y el nombre que llega al backend es
        // literalmente `facetFilters[]`: el DTO corre con `forbidNonWhitelisted`, así que
        // contesta 400 *"property facetFilters[] should not exist"* y la tabla se queda
        // sólo con los borradores. El contrato es la clave repetida sin corchetes, que es
        // lo que `indexes: null` produce.
        paramsSerializer: { indexes: null },
      },
    );

    setHits(data.hits ?? []);
    // **Ausente se lee como `viewer`**: con el API viejo la pantalla queda en sólo
    // lectura en vez de ofrecer botones que van a dar 403. Falla cerrado.
    setNivel(data.myCatalogLevel ?? "viewer");
    setTotal(data.nbHits ?? 0);
    setFacets(data.facets ?? EMPTY_FACETS);
  }, [filters, page, pageSize]);

  const fetchDrafts = useCallback(async () => {
    // **Sin `state`**: el endpoint devuelve borradores y pendientes juntos, que es lo que
    // la vista única necesita. El estado pasa a ser una columna que filtra, no una pestaña
    // que hay que adivinar.
    // **Un 403 acá no es un error de pantalla: es que este nivel no tiene borradores.**
    // Trabajar borradores es de Editor para arriba, así que un `viewer` o un
    // `by_relation` reciben 403 en esta llamada mientras el listado publicado les
    // funciona perfecto. Dejarlo caer en el catch de abajo pintaba "No tienes permisos
    // para realizar esta acción" en rojo sobre una pantalla que andaba bien.
    const data = await axiosInstance
      .get<DraftRow[]>(API_URL_PIM_DRAFTS)
      .then((r) => r.data)
      .catch((e) => {
        if (e?.response?.status === 403) return null;
        throw e;
      });

    // Sin permiso no hay nada que mezclar, y **el total no se toca**: lo puso el listado
    // publicado, que para este nivel es la única fuente.
    if (data === null) {
      setDrafts([]);
      return;
    }

    // El filtrado de las dos vistas de borradores es en memoria a propósito: son cientos
    // de filas, no cientos de miles, y su fuente no tiene facetas. Filtrar en el servidor
    // sería prometer una capacidad que el endpoint no tiene.
    const texto = filters.search.trim().toLowerCase();
    const porTexto = texto
      ? data.filter(
          (row) =>
            row.sku.toLowerCase().includes(texto) ||
            (row.name ?? "").toLowerCase().includes(texto) ||
            (row.manufacturer ?? "").toLowerCase().includes(texto),
        )
      : data;

    const soloSinPublicar =
      filters.estado === "draft" || filters.estado === "review";
    const filtradas = soloSinPublicar
      ? porTexto.filter((row) => row.state === filters.estado)
      : porTexto;

    setDrafts(filtradas);
    // **El total lo pone `fetchPublished`, salvo en la vista de sólo borradores/pendientes.**
    // Las dos fetch corren en paralelo (`Promise.all`) y ambas son requests HTTP: sin este
    // guard, cualquiera de las dos podía resolver última y pisarle el total a la otra, y el
    // paginador terminaba mostrando a veces la cantidad de borradores en vez de la de
    // publicados — la paginación de la tabla es la de los publicados (ver comentario en
    // `pagination.total` de list.tsx).
    if (soloSinPublicar) setTotal(filtradas.length);
  }, [filters.search, filters.estado]);

  /**
   * El contador se re-pide al cambiar de vista y no una sola vez al montar: volver de
   * aprobar una versión es, justamente, cuando el número cambió.
   */
  useEffect(() => {
    let cancelado = false;
    axiosInstance
      .get<{ count: number }>(`${API_URL_PIM_DRAFTS}/queue-count`)
      .then(({ data }) => !cancelado && setQueueCount(data.count ?? 0))
      // Sin contador el segmented igual funciona: es un badge, no una función.
      // Mismo criterio que los borradores: sin permiso, la cola no es un error, es cero.
      .catch(() => undefined);
    return () => {
      cancelado = true;
    };
    // Una sola vez al montar y cuando algo pudo cambiarlo: la cola es de todos, no de la
    // vista en la que uno esté parado.
  }, []);

  useEffect(() => {
    let cancelado = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        // **Las dos fuentes, salvo que el filtro las excluya.** Filtrar por `borrador` no
        // tiene por qué pegarle a Algolia, y filtrar por `activo` no tiene por qué leer la
        // tabla de pendientes: el filtro de estado decide a quién se le pregunta.
        const soloSinPublicar =
          filters.estado === "draft" || filters.estado === "review";
        // Con un estado sin publicar manda el estado: si quedaron filtros de índice de
        // antes, se ignoran en vez de dejar la tabla vacía sin pedir nada.
        const soloPublicados =
          !soloSinPublicar &&
          (esEstadoPublicado(filters.estado) || filtraPorIndice(filters));

        await Promise.all([
          soloSinPublicar ? Promise.resolve() : fetchPublished(),
          soloPublicados ? Promise.resolve() : fetchDrafts(),
        ]);

        if (soloSinPublicar) setHits([]);
        if (soloPublicados) setDrafts([]);
      } catch (e) {
        if (!cancelado) {
          setError(
            (e as { response?: { data?: { message?: string } } })?.response
              ?.data?.message ?? "No se pudo cargar el catálogo",
          );
        }
      } finally {
        if (!cancelado) {
          setLoading(false);
          setPrimeraCarga(false);
        }
      }
    };

    run();
    return () => {
      cancelado = true;
    };
  }, [filters, fetchPublished, fetchDrafts]);

  /** Cambiar de vista o de filtro vuelve a la página 1: si no, queda vacía. */
  const applyFilters = useCallback((next: Partial<CatalogFilters>) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setPage(1);
  }, []);

  const facetValues = useMemo(
    () => (name: string) =>
      Object.entries(facets[name] ?? {})
        .sort((a, b) => b[1] - a[1])
        .map(([value, count]) => ({ value, count })),
    [facets],
  );

  return {
    loading,
    primeraCarga,
    error,
    nivel,
    /** Puede editar, importar, aprobar y publicar CUALQUIER producto. */
    puedeEscribirTodo: nivel === "editor" || nivel === "admin",
    /**
     * Puede crear: cualquiera que figura en Permisos del Catálogo, sea cual sea su nivel
     * (xcons-docs#594). Quien crea queda Admin y dueño de lo que crea.
     */
    puedeCrear: nivel !== "no_access",
    /** Puede tocar el EAV y repartir el permiso. */
    esAdminDeCatalogo: nivel === "admin",
    hits,
    drafts,
    total,
    queueCount,
    page,
    pageSize,
    filters,
    facetValues,
    setPage,
    setPageSize,
    applyFilters,
  };
};
